import React from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import {
  Button,
  ButtonProps,
  Icon,
  IconName,
  Theme,
  colors,
  layout,
  media,
} from "@literati/public-ui-shared-react";
import LiteratiLogoDark from "@/atoms/Icon/literati-dark";

/** This is the className that will be pre-pended to all of StepHeader's inner component classNames */
export const STEP_HEADER_CLASS_BASE = "step-header";

/** These are all of the internal classNames that are used within the StepHeader component */
export const StepHeaderClassName = {
  BASE: STEP_HEADER_CLASS_BASE,
  CONTENT: `${STEP_HEADER_CLASS_BASE}-content`,
} as const;

export const HEADER_HEIGHT_PX = "60px";
export const HEADER_HEIGHT = 60;

const Header = styled.header`
  display: flex;
  justify-content: center;

  z-index: 50;
  width: 100vw;
  height: ${HEADER_HEIGHT_PX};

  background: ${colors.white};
  box-shadow: 0px 0px 15px ${colors.newsprint1};
`;

const Content = styled.div`
  ${layout.contentSpacing};

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 1rem 0;
  width: 100vw;
  height: 100%;
`;

const BackButton = styled(Button)<{ $suppressBackButton: boolean }>`
  display: ${({ $suppressBackButton }) => $suppressBackButton && "none"};
  position: absolute;
  left: 1.25rem;

  padding: 0 1rem;
  margin: 0 0 0 -1rem;
  width: auto;

  ${media.sm} {
    left: 3rem;
  }

  ${media.md} {
    display: none;
  }
`;

const LogoButton = styled(Button)`
  padding: 0 1rem;
  width: auto;
`;

export interface StepHeaderProps {
  backButtonProps?: ButtonProps;
  className?: string;
  logoButtonProps?: ButtonProps;
  suppressBackButton?: boolean;
}

export const StepHeader = ({
  backButtonProps = {},
  className = "",
  logoButtonProps,
  suppressBackButton = false,
}: StepHeaderProps) => {
  const cls = StepHeaderClassName;

  const router = useRouter();

  return (
    <Theme>
      <Header className={`${cls.BASE} ${className}`}>
        <Content className={cls.CONTENT}>
          <BackButton
            onClick={() => router.back()}
            $suppressBackButton={suppressBackButton}
            variant="unstyled"
            type="button"
            {...backButtonProps}
          >
            {backButtonProps.children || <Icon name={IconName.CHEVRON_LEFT} />}
          </BackButton>
          <LogoButton
            onClick={() => router.push("/")}
            variant="unstyled"
            {...logoButtonProps}
          >
            {logoButtonProps?.children || <LiteratiLogoDark />}
          </LogoButton>
        </Content>
      </Header>
    </Theme>
  );
};

export default StepHeader;
